import { AxiosInstance, AxiosRequestConfig } from "axios";
import { CompanyInfo, SuggestedCompany } from "../types/CompanyInfo";
import { LoggedOnUserInfoDTO, User } from "../types/UserInfo";
import {
  CompanySubsidiary,
  ExtendedShareholderDTO,
  NewShareholderResponse,
  ShareholderDetailsResponseDTO,
  ShareholderField,
  ShareholdersInfo,
  ShareholderType,
  ShareholderViewResponse,
  transformShareholderInfo,
  UpdateShareholderDTO,
} from "../types/Shareholder";
import {
  CancellationOfShares,
  ConversionFromISINDTO,
  ConversionOfSharesDTO,
  IncreaseNominalValueDTO,
  IssueNewSharesDTO,
  LiquidationDTO,
  MergerDTO,
  NewTransaction,
  PayDividend,
  ReductionOfNominalValue,
  RegisterGroupContribution,
  ShareholdingPerDateDTO,
  ShareReverseSplit,
  ShareSplitTransaction,
  Transaction,
  TransactionAllShares,
} from "../types/Transactions";
import {
  ParsedShareholder,
  ParseExcelResponse,
  ShareholderIdShares,
} from "../types/excelImportDomain";
import { ArticlesOfAssociation } from "../types/ArticlesOfAssociation";
import {
  BatchInvitation,
  Invitation,
  InvitationRequest,
  InvitationType,
} from "../types/Invitation";
import {
  CreateProjectRequest,
  CreateProjectResponse,
  GetProjectListResponse,
} from "../dbot/common/commonDomain";
import {
  GetSubscriptionFormSigningStatusResponse,
  SignSubscriptionForm,
  SignSubscriptionFormShare,
} from "../views/Pages/SubscriptionFormSign/subscriptionFormDomain";
import { downloadFileContent } from "./DownloadService";
import { FileUploadConfig, UploadedAttachment } from "../fileUpload/fileUploadDomain";
import { OnboardingCandidateResponse } from "../stores/PortfolioStore";
import { GetMeetingEmailDocumentResponse } from "../noticeForBoardMember/noticeForBoardMemberDomain";
import { Language } from "../i18n/languages";
import { CreateMeetingRequest } from "../dbot/project/projectDomain";
import {
  PortfolioShareholdersResponse,
  SelectedPortfolioOption,
  toPortfolioApiUrl,
  toPortfolioContactsApiUrl,
} from "../portfolio/portfolioDomain";
import {
  GetAdminsInCompaniesResponse,
  GetPortfolioCustomRoleRequest,
  RevokeAllRequest,
  UpdateAdminEmailRequest,
} from "../types/Settings";
import {
  ChangePermissionsRequest,
  GetPermissionsResponse,
  ObjectPermission,
} from "../types/Permissions";
import {
  CreateShareholderCommitmentRequest,
  ShareholderCommitment,
  ShareholderContribution,
  ShareholderPledgeForm,
  ShareholderRemarksForm,
} from "../shareholderRegister/shareholderRegisterDomain";
import { BeneficialOwner } from "../shareholderRegister/beneficialOwners/beneficialOwnersDomain";
import { downloadFile, getResponseFilename } from "./api";
import { ShareType } from "../common/share";
import { Actor, SelectedActor } from "../types/SelectActors";
import { EnterpriseAccountMetaData, EnterpriseAccountStatus, Portfolio } from "../common/common";
import { DocumentsToSignResponse } from "../signing/signDocumentDomain";
import { toQueryString } from "../utils/queryParams";

export class ApiService {
  private http: AxiosInstance;

  constructor(axiosInstance: AxiosInstance) {
    this.http = axiosInstance;
  }

  createUser(
    username: string,
    name: string,
    password: string,
    csrfToken: string,
  ): Promise<{ user: User; redirectTo: string }> {
    return this.http
      .post(`/auth/user/new`, { "X-CSRF-TOKEN": csrfToken, username, name, password })
      .then((res) => res.data);
  }

  logon(username: string, password: string, csrfToken: string): Promise<{ user: User }> {
    return this.http
      .post(`/auth/login`, { "X-CSRF-TOKEN": csrfToken, username, password })
      .then((res) => res.data);
  }

  async passwordResetRequest(email: string): Promise<void> {
    await this.http.post("/auth/login/password-reset-request", { email });
  }

  async checkPasswordResetToken(token: string): Promise<void> {
    await this.http.post("/auth/login/password-reset-check-token", { token });
  }

  async passwordResetSetNewPassword(token: string, newPassword: string): Promise<void> {
    const res = await this.http.post("/auth/login/password-reset-set-new-password", {
      token,
      newPassword,
    });
    return res.data;
  }

  postOneTimeCode(otp: string): Promise<void> {
    return this.http.post("/auth/login/otp", { otp }).then((res) => res.data);
  }

  sendNewOneTimeCode(): Promise<void> {
    return this.http.post("/auth/login/send-new-otp").then((res) => res.data);
  }

  authStatus(): Promise<void> {
    return this.http.get("/auth/status").then((res) => res.data);
  }

  getCsrfToken(headers?: AxiosRequestConfig["headers"]): Promise<string> {
    return this.http.get(`/index`, { headers }).then((res) => res.data);
  }

  fetchUserInfo(headers?: AxiosRequestConfig["headers"]): Promise<LoggedOnUserInfoDTO> {
    return this.http.get(`/auth/getloggedonuser`, { headers }).then((res) => res.data);
  }

  fetchCompanyInfo(
    orgNumber: string,
    options: { updateLastVisited?: boolean } = {},
  ): Promise<CompanyInfo> {
    return this.http.get(`/company/${orgNumber}${toQueryString(options)}`).then((res) => res.data);
  }

  fetchShareholderView(
    orgNumber: string,
    shareholderId: number,
    options: { updateLastVisited?: boolean } = {},
  ): Promise<ShareholderViewResponse> {
    return this.http
      .get(`/shareholder-view/${orgNumber}/${shareholderId}${toQueryString(options)}`)
      .then((res) => res.data);
  }

  updateShareholderInfo(shareholderId: number, shareholderDTO: UpdateShareholderDTO) {
    return this.http
      .post(`/shareholder-view/update-shareholder/${shareholderId}`, shareholderDTO)
      .then((res) => res.data);
  }

  fetchProjects(orgNumber: string): Promise<GetProjectListResponse> {
    return this.http.get(`/projects/${orgNumber}`).then((res) => res.data);
  }

  fetchMeetingForBoardMember(
    orgNo: string,
    projectId: string,
  ): Promise<GetMeetingEmailDocumentResponse> {
    return this.http
      .get(`/boardmember-view/${orgNo}/meetings/${projectId}`)
      .then((res) => res.data);
  }

  deleteProject(projectId: string): Promise<void> {
    return this.http.delete(`/project/${projectId}`).then((res) => res.data);
  }

  newProject(projectToCreate: CreateProjectRequest): Promise<CreateProjectResponse> {
    return this.http.post(`/projects`, projectToCreate).then((res) => res.data);
  }

  newMeeting(body: CreateMeetingRequest): Promise<CreateProjectResponse> {
    return this.http.post("/projects/new-meeting", body).then((res) => res.data);
  }

  logout(): Promise<void> {
    return this.http.get(`/auth/logout`).then((res) => res.data);
  }

  registerAdminUser(orgNumber: string): Promise<void> {
    return this.http.post(`/company/register-admin-user/${orgNumber}`).then((res) => res.data);
  }

  articlesOfAssociation(orgNumber: string): Promise<ArticlesOfAssociation[]> {
    return this.http.get(`/articleofassociation/list/${orgNumber}`).then((res) => res.data);
  }

  updateArticlesOfAssociation(articles: ArticlesOfAssociation, orgNumber: string): Promise<void> {
    return this.http.put(`/articlesofassociation/${orgNumber}`, articles).then((res) => res.data);
  }

  updateArticlesOfAssociationDate(date: string, orgNumber: string): Promise<void> {
    return this.http
      .post(`/articlesofassociation/${orgNumber}/date`, { date })
      .then((res) => res.data);
  }

  downloadArticlesOfAssociationAsPDF(orgNo: string): Promise<void> {
    return this.http
      .get(`/articlesofassociation/${orgNo}/download`, {
        responseType: "blob",
      })
      .then((response) => {
        downloadFile(response.data, getResponseFilename(response));
      });
  }

  async fetchShareholdersAll(orgNo: string, timestamp?: string): Promise<ShareholdersInfo> {
    return transformShareholderInfo(
      await this.http
        .get(`/shareholder/all/${orgNo}`, timestamp ? { params: { timestamp } } : undefined)
        .then((res) => res.data),
    );
  }

  fetchShareholdersAsActors(orgNo: string): Promise<Actor[]> {
    return this.http.get(`/shareholder/${orgNo}/actors`).then((res) => res.data);
  }

  addShareholdersFromActors(
    orgNo: string,
    actors: SelectedActor[],
  ): Promise<ExtendedShareholderDTO[]> {
    return this.http.post(`/shareholder/${orgNo}/actors`, actors).then((res) => res.data);
  }

  runAutoimport(orgNumber: string | number): Promise<void> {
    return this.http.post(`/company/reimportshaholderdata/${orgNumber}`).then((res) => res.data);
  }

  inviteShareholders(
    orgNumber: string,
    shareholders: ShareholderNotification[],
    language: Language,
    personalNoteText?: string,
  ): Promise<void> {
    return this.http
      .post(`/invitation/${orgNumber}/invite-shareholders`, {
        shareholders,
        language,
        personalNoteText,
      })
      .then((res) => res.data);
  }

  async fetchShareholderById(id: number): Promise<ShareholderDetailsResponseDTO> {
    return this.http.get(`/shareholder/${id}`).then((res) => res.data);
  }

  fetchTransactionLog(orgNumber: string | number): Promise<Transaction[]> {
    return this.http.get(`/transactionlog/all/${orgNumber}`).then((res) => res.data);
  }

  fetchTransactionById(id: string | number): Promise<Transaction> {
    return this.http.get(`/transactionlog/${id}`).then((res) => res.data);
  }

  async getCompanies(partName: string): Promise<SuggestedCompany[]> {
    return this.http.get(`/companysearch`, { params: { q: partName } }).then((res) => res.data);
  }

  async searchSwedishBusinessRegister(query: string): Promise<SuggestedCompany[]> {
    return this.http
      .get(`/search-swedish-companies`, { params: { q: query } })
      .then((res) => res.data);
  }

  async askForPermission(payload: {
    organisationNumber: string;
    companyName: string;
    email: string;
  }): Promise<void> {
    return this.http.post(`/invitation/ask-for-permission`, payload).then((res) => res.data);
  }

  async findOnboardingCandidates(organisationNumber: string): Promise<OnboardingCandidateResponse> {
    return this.http
      .get(`/company-roles/find-onboarding-candidates/${organisationNumber}`)
      .then((response) => {
        return response.data;
      });
  }

  async createTransfer(transaction: NewTransaction, transactionId?: string): Promise<number> {
    return this.http
      .post(`/transaction/` + (transactionId ? transactionId : "transfer"), transaction)
      .then((r) => r.data);
  }

  async transferAllShares(
    transaction: TransactionAllShares,
    transactionId: string | undefined,
  ): Promise<number> {
    const response = await this.http.post(
      `/transaction/` + (transactionId ? transactionId : "sellall"),
      transaction,
    );
    return response.data;
  }

  increaseNominalValue(transaction: IncreaseNominalValueDTO["data"]): Promise<number> {
    return this.http.post(`/transaction/increasenominalvalue`, transaction).then((res) => res.data);
  }

  issueShares(transaction: IssueNewSharesDTO["data"]): Promise<number> {
    return this.http.post(`/transaction/issue`, transaction).then((res) => res.data);
  }

  shareSplit(transaction: ShareSplitTransaction, transactionId?: string): Promise<number> {
    return this.http
      .post(`/transaction/` + (transactionId ? transactionId : "sharesplit"), transaction)
      .then((res) => res.data);
  }

  shareReverseSplit(transaction: ShareReverseSplit, transactionId?: string): Promise<number> {
    return this.http
      .post(`/transaction/` + (transactionId ? transactionId : "sharereversesplit"), transaction)
      .then((res) => res.data);
  }

  shareCancellation(transaction: CancellationOfShares): Promise<number> {
    return this.http.post(`/transaction/sharecancellation`, transaction).then((res) => res.data);
  }

  reductionOfNominalValue(transaction: ReductionOfNominalValue): Promise<number> {
    return this.http
      .post(`/transaction/reductionofnominalvalue`, transaction)
      .then((res) => res.data);
  }

  payDividend(transaction: PayDividend): Promise<number> {
    return this.http.post(`/transaction/paydividend`, transaction).then((res) => res.data);
  }

  registerGroupContribution(transaction: RegisterGroupContribution): Promise<number> {
    return this.http
      .post(`/transaction/registergroupcontribution`, transaction)
      .then((res) => res.data);
  }

  conversionOfShares(transaction: ConversionOfSharesDTO["data"]): Promise<number> {
    return this.http.post(`/transaction/conversionofshares`, transaction).then((res) => res.data);
  }

  conversionFromISIN(transaction: ConversionFromISINDTO["data"]): Promise<number> {
    return this.http.post(`/transaction/conversionfromisin`, transaction).then((res) => res.data);
  }

  shareholdingPerDate(transaction: ShareholdingPerDateDTO["data"]): Promise<number> {
    return this.http.post(`/transaction/shareholdingperdate`, transaction).then((res) => res.data);
  }

  async liquidation(transaction: LiquidationDTO["data"]): Promise<number> {
    const res = await this.http.post(`/transaction/liquidation`, transaction);
    return await res.data;
  }

  async createMerger(transaction: MergerDTO["data"]): Promise<number> {
    const res = await this.http.post(`/transaction/merger`, transaction);
    return await res.data;
  }

  uploadTransactionAttachment(
    transactionlogId: number,
    form: FormData,
  ): Promise<UploadedAttachment[]> {
    return this.http
      .post(`/transactionlog/${transactionlogId}/upload-attachment`, form)
      .then((res) => res.data);
  }

  deleteTransactionAttachment(transactionlogId: number, attachmentId: string): Promise<void> {
    return this.http
      .delete(`/transactionlog/${transactionlogId}/attachment/${attachmentId}`)
      .then((res) => res.data);
  }

  getDocumentsToSign(): Promise<DocumentsToSignResponse> {
    return this.http.get(`/sign/documents-to-sign`).then((res) => res.data);
  }

  getSigningBatchDocuments(batchId: string): Promise<DocumentsToSignResponse> {
    return this.http.get(`/sign/batch/${batchId}`).then((res) => res.data);
  }

  getSubscriptionFormDetails(documentReference: string): Promise<SignSubscriptionForm> {
    return this.http
      .get(`/sign/subscription-form-details/${documentReference}`)
      .then((res) => res.data);
  }

  getSubscriptionFormSigningStatus(
    documentReference: string,
  ): Promise<GetSubscriptionFormSigningStatusResponse> {
    return this.http
      .get(`/sign/check-subscription-signing-status/${documentReference}`)
      .then((res) => res.data);
  }

  updateSubscriptionFormDetails(
    documentReference: string,
    subscription: SignSubscriptionFormShare[],
  ): Promise<void> {
    return this.http
      .post(`/sign/update-subscription-form/${documentReference}`, subscription)
      .then((res) => res.data);
  }

  signWithEmail(documentReference: string): Promise<void> {
    return this.http.get(`/sign/sign-with-email/${documentReference}`).then((res) => res.data);
  }

  signVerifyWithCode(documentReference: string, authorizationCode: string): Promise<void> {
    return this.http
      .post(`/sign/verify-signing-with-code/${documentReference}/${authorizationCode}`)
      .then((res) => res.data);
  }

  signWithHandWriting(documentReference: string, formData: FormData): Promise<void> {
    return this.http
      .post(`/sign/sign-with-handwriting/${documentReference}`, formData, {
        headers: { "Content-Type": "multipart/form-data" },
      })
      .then((res) => res.data);
  }

  editShareType(shareType: Partial<ShareType>, sharetypeId: number): Promise<void> {
    return this.http.post(`/sharetype/edit/${sharetypeId}`, shareType).then((res) => res.data);
  }

  createShareType(shareType: Partial<ShareType>): Promise<void> {
    return this.http.post(`/sharetype/new`, shareType).then((res) => res.data);
  }

  deleteShareType(sharetypeId: number, orgNumber: string | number): Promise<void> {
    return this.http.delete(`/sharetype/${sharetypeId}/${orgNumber}`).then((res) => res.data);
  }

  fetchPermissions(orgNo: string, objectId: string): Promise<GetPermissionsResponse> {
    return this.http.get(`/permission/${orgNo}/${objectId}`).then((res) => res.data);
  }

  changePermissions(orgNo: string, request: ChangePermissionsRequest): Promise<ObjectPermission[]> {
    return this.http.post(`/permission/${orgNo}`, request).then((res) => res.data);
  }

  sendInvitation({
    invitationType,
    orgNo,
    email,
    language,
    shareholderId,
    roleId,
    personalNoteText,
  }: {
    invitationType: InvitationType;
    orgNo: string;
    email: string;
    language: string;
    shareholderId?: number;
    roleId?: string;
    personalNoteText?: string;
  }): Promise<void> {
    return this.http
      .post(`/invitation/${orgNo}/${invitationType}/invite`, {
        email,
        language,
        shareholderId,
        roleId,
        personalNoteText,
      })
      .then((res) => res.data);
  }

  inviteAdministratorOrCustomeRole({
    email,
    language,
    inviteTo,
    inviteToRoleId,
  }: {
    email: string;
    language: Language;
    inviteTo: string[];
    inviteToRoleId?: string;
  }): Promise<void> {
    return this.http
      .post(`/portfolio/admins/invite`, { email, language, inviteTo, inviteToRoleId })
      .then((res) => res.data);
  }

  fetchInvitation(invitationId: string, invitationToken: string): Promise<Invitation> {
    return this.http.get(`/invitation/${invitationId}/${invitationToken}`).then((res) => res.data);
  }

  acceptInvitation(invitationId: string, invitationToken: string): Promise<void> {
    return this.http
      .post(`/invitation/${invitationId}/${invitationToken}/accept`)
      .then((res) => res.data);
  }

  fetchBatchInvitation(invitationId: string, invitationToken: string): Promise<BatchInvitation> {
    return this.http
      .get(`/batch-invitation/${invitationId}/${invitationToken}`)
      .then((res) => res.data);
  }

  acceptBatchInvitation(invitationId: string, invitationToken: string): Promise<void> {
    return this.http
      .post(`/batch-invitation/${invitationId}/${invitationToken}/accept`)
      .then((res) => res.data);
  }

  fetchInvitationRequest(invitationRequestId: string): Promise<InvitationRequest> {
    return this.http
      .get(`/invitation/invitation-request/${invitationRequestId}`)
      .then((res) => res.data);
  }

  rejectInvitationRequest(invitationRequestId: string): Promise<void> {
    return this.http
      .post(`/invitation/invitation-request/${invitationRequestId}/reject`)
      .then((res) => res.data);
  }

  fetchBatchInvitationRequest(batchId: string): Promise<InvitationRequest[]> {
    return this.http.get(`/invitation/batch-invitation-request/${batchId}`).then((res) => res.data);
  }

  acceptBatchInvitationRequest(invitationRequestId: string): Promise<void> {
    return this.http
      .post(`/invitation/batch-invitation-request/${invitationRequestId}/accept`)
      .then((res) => res.data);
  }

  acceptInvitationRequest(invitationRequestId: string): Promise<void> {
    return this.http
      .post(`/invitation/invitation-request/${invitationRequestId}/accept`)
      .then((res) => res.data);
  }

  rejectBatchInvitationRequest(invitationRequestId: string): Promise<void> {
    return this.http
      .post(`/invitation/batch-invitation-request/${invitationRequestId}/reject`)
      .then((res) => res.data);
  }

  sendEmailVerificationCode(emailAddress: string): Promise<void> {
    return this.http
      .post(`/user/send-verification-email`, { emailAddress })
      .then((res) => res.data);
  }

  updateShareholderPledge(id: number, form: ShareholderPledgeForm) {
    return this.http.put(`/shareholder/${id}/pledge`, form.toData()).then((res) => res.data);
  }

  updateShareholderRemark(id: number, form: ShareholderRemarksForm) {
    return this.http.put(`/shareholder/${id}/remark`, form.toData()).then((res) => res.data);
  }

  markAutoImportMessageAsRendered(organisationNumber: string): Promise<void> {
    return this.http
      .put(`/company/confirm-autoimport/${organisationNumber}`)
      .then((res) => res.data);
  }

  markAutoImportMessageAsNotRendered(organisationNumber: string): Promise<void> {
    return this.http
      .delete(`/company/confirm-autoimport/${organisationNumber}`)
      .then((res) => res.data);
  }

  updateUserLanguage(newLanguage: Language): Promise<void> {
    return this.http.put(`/user/language/${newLanguage}`).then((res) => res.data);
  }

  acceptCompanyTerms(organisationNumber: string): Promise<void> {
    return this.http.post(`/company/accept-terms/${organisationNumber}`).then((res) => res.data);
  }

  parseShareholdersExcel(orgNo: string, formData: FormData): Promise<ParseExcelResponse> {
    return this.http.post(`/shareholder/${orgNo}/parse-excel`, formData).then((res) => res.data);
  }

  uploadShareholdersExcel(
    orgNo: string,
    formData: FormData,
  ): Promise<UploadShareholdersExcelResponse> {
    return this.http
      .post(`/shareholder/${orgNo}/upload-shareholders`, formData)
      .then((res) => res.data);
  }

  uploadPortfolioShareholdersExcel(
    formData: FormData,
    selectedPortfolio: SelectedPortfolioOption,
  ): Promise<UploadShareholdersExcelResponse> {
    const url =
      selectedPortfolio.type === "portfolio-instance"
        ? `/portfolio/${selectedPortfolio.portfolio.id}/upload-shareholders`
        : `/portfolio/upload-shareholders`;

    return this.http.post(url, formData).then((res) => res.data);
  }

  importShareholders(
    orgNo: string,
    shareholders: ParsedShareholder[],
  ): Promise<ShareholderIdShares> {
    return this.http
      .post(`/shareholder/${orgNo}/import-shareholders`, {
        shareholders,
      })
      .then((res) => res.data);
  }

  storeShareholderField(id: number, name: ShareholderField, value: string): Promise<unknown> {
    return this.http
      .post(`/shareholder/update`, {
        shareholderId: id,
        fieldName: ShareholderField[name],
        newValue: value,
      })
      .then((res) => res.data);
  }

  addNewShareholder(
    orgNo: string,
    shareholderType: ShareholderType,
  ): Promise<NewShareholderResponse> {
    return this.http
      .post(`/shareholder/new-shareholder`, {
        shareholderType: shareholderType,
        orgNo: orgNo,
      })
      .then((res) => res.data);
  }

  addAffiliateToSession(affiliateKey: string): Promise<void> {
    return this.http.put(`/add-affiliate/${affiliateKey}`).then((res) => res.data);
  }

  deleteShareholderHard(shareholderId: number): Promise<unknown> {
    return this.http.delete(`/shareholder/delete-hard/${shareholderId}`).then((res) => res.data);
  }

  fetchTrankSubsidiaries(organisationNumber: string): Promise<CompanySubsidiary[]> {
    return this.http.get(`/company/subsidiaries/${organisationNumber}`).then((res) => res.data);
  }

  deleteTransaction(orgNumber: string, transactionlogId: number) {
    return this.http
      .delete(`/transaction/${orgNumber}/${transactionlogId}`)
      .then((res) => res.data);
  }

  updateShareholder(shareholderId: number, shareholderDTO: UpdateShareholderDTO) {
    return this.http.post(`/shareholder/${shareholderId}`, shareholderDTO).then((res) => res.data);
  }

  insertShareholder(shareholderDTO: UpdateShareholderDTO): Promise<ExtendedShareholderDTO> {
    return this.http.post(`/shareholder/new`, shareholderDTO).then((res) => res.data);
  }

  changeTransactionDate(orgNumber: string, transactionlogId: number, date: string) {
    return this.http
      .post(`/transaction/${orgNumber}/${transactionlogId}`, { transactionDate: date })
      .then((res) => res.data);
  }

  createPortfolio(portfolioName: string): Promise<Portfolio> {
    return this.http.post(`/portfolio/new`, { portfolioName }).then((res) => res.data);
  }

  createSigningDocumentBatch(documentReferences: string[]): Promise<string> {
    return this.http.post(`/sign/create-batch`, documentReferences).then((res) => res.data);
  }

  verifyUserEmail(emailAddress: string, verificationCode: string) {
    return this.http
      .post(`/user/verify-email`, { emailAddress, verificationCode })
      .then((res) => res.data);
  }

  getPortfolioShareholders(
    portfolio: SelectedPortfolioOption,
  ): Promise<PortfolioShareholdersResponse> {
    return this.http.get(`${toPortfolioApiUrl(portfolio)}/shareholders`).then((res) => res.data);
  }

  getPortfolioAdmins(portfolio: SelectedPortfolioOption): Promise<GetAdminsInCompaniesResponse> {
    return this.http.get(`${toPortfolioContactsApiUrl(portfolio)}/admins`).then((res) => res.data);
  }

  getPortfolioCustomRole(
    portfolio: SelectedPortfolioOption,
    portfolioId?: string,
  ): Promise<GetPortfolioCustomRoleRequest> {
    const url =
      portfolioId === undefined
        ? `${toPortfolioContactsApiUrl(portfolio)}/custom-roles`
        : `/portfolio/custom-roles?portfolioId=${portfolioId}`;

    return this.http.get(url).then((res) => res.data);
  }

  openPreviewCustomRole(roleId: string, orgNo: string) {
    return this.http
      .get(`company-settings/${orgNo}/preview-custom-role/${roleId}`)
      .then((res) => res.data);
  }

  updateAdministratorEmail({
    email,
    externalId,
    customRoleId,
    portfolioId,
  }: UpdateAdminEmailRequest): Promise<UpdateAdminEmailRequest> {
    return this.http
      .post(`/portfolio/admins/update-email`, {
        email,
        externalId,
        customRoleId,
        portfolioId,
      })
      .then((res) => res.data);
  }

  revokeAdminAccess(organizationNumber: string, externalId: string) {
    return this.http.delete(`/company-settings/${organizationNumber}/admin-access/${externalId}`);
  }

  revokeAdminAccessAll(externalId: string, orgNos: string[]): Promise<RevokeAllRequest[]> {
    return this.http
      .post(`/portfolio/admins/revoke-all`, {
        externalId,
        orgNos,
      })
      .then((res) => res.data);
  }

  registerShareholderContribution(shareholderContribution: ShareholderContribution) {
    return this.http
      .post(`/shareholderContribution/new`, shareholderContribution)
      .then((res) => res.data);
  }

  fetchShareholderContributions(organisationNumber: string): Promise<ShareholderContribution[]> {
    return this.http.get(`/shareholderContribution/${organisationNumber}`).then((res) => res.data);
  }

  fetchSingleShareholderContributions(
    organisationNumber: string,
    contributionId: number,
  ): Promise<ShareholderContributionDetailsResponse> {
    return this.http
      .get(`/shareholderContribution/${organisationNumber}/${contributionId}`)
      .then((res) => res.data);
  }

  uploadContributionAttachments(
    orgNumber: string,
    reference: string,
    form: FormData,
  ): Promise<UploadedAttachment[]> {
    return this.http
      .post(`/shareholderContribution/${orgNumber}/${reference}/attachments`, form)
      .then((res) => res.data);
  }

  downloadContributionAttachment(
    organisationNumber: string,
    reference: string,
    attachmentId: string,
  ) {
    return downloadFileContent(
      `shareholderContribution/${organisationNumber}/${reference}/attachments/${attachmentId}`,
      this.http,
    );
  }

  deleteContributionAttachment(
    organisationNumber: string,
    reference: string,
    attachmentId: string,
  ) {
    return this.http
      .delete(
        `/shareholderContribution/${organisationNumber}/${reference}/attachments/${attachmentId}`,
      )
      .then((res) => res.data);
  }

  changeContributionDate(
    organisationNumber: string,
    contributionId: number,
    registrationDate: string,
  ) {
    return this.http
      .post(`/shareholderContribution/${organisationNumber}/${contributionId}/changeDate`, {
        registrationDate,
      })
      .then((res) => res.data);
  }

  deleteContribution(organisationNumber: string, contributionId: number) {
    return this.http
      .delete(`/shareholderContribution/${organisationNumber}/${contributionId}`)
      .then((res) => res.data);
  }

  registerShareholderCommitment(shareholderCommitment: CreateShareholderCommitmentRequest) {
    return this.http
      .post(`/shareholder-commitment/new`, shareholderCommitment)
      .then((res) => res.data);
  }

  fetchShareholderCommitments(organisationNumber: string): Promise<ShareholderCommitment[]> {
    return this.http.get(`/shareholder-commitment/${organisationNumber}`).then((res) => res.data);
  }

  fetchSingleShareholderCommitments(
    organisationNumber: string,
    commitmentId: string,
  ): Promise<ShareholderCommitmentDetailsResponse> {
    return this.http
      .get(`/shareholder-commitment/${organisationNumber}/${commitmentId}`)
      .then((res) => res.data);
  }

  uploadCommitmentAttachments(
    orgNumber: string,
    commitmentId: string,
    form: FormData,
  ): Promise<UploadedAttachment[]> {
    return this.http
      .post(`/shareholder-commitment/${orgNumber}/${commitmentId}/attachments`, form)
      .then((res) => res.data);
  }

  downloadCommitmentAttachment(
    organisationNumber: string,
    commitmentId: string,
    attachmentId: string,
  ) {
    return downloadFileContent(
      `shareholder-commitment/${organisationNumber}/${commitmentId}/attachments/${attachmentId}`,
      this.http,
    );
  }

  deleteCommitmentAttachment(
    organisationNumber: string,
    commitmentId: string,
    attachmentId: string,
  ) {
    return this.http
      .delete(
        `/shareholder-commitment/${organisationNumber}/${commitmentId}/attachments/${attachmentId}`,
      )
      .then((res) => res.data);
  }

  changeCommitmentDate(organisationNumber: string, commitmentId: string, registrationDate: string) {
    return this.http
      .post(`/shareholder-commitment/${organisationNumber}/${commitmentId}/changeDate`, {
        registrationDate,
      })
      .then((res) => res.data);
  }

  deleteCommitment(organisationNumber: string, commitmentId: string) {
    return this.http
      .delete(`/shareholder-commitment/${organisationNumber}/${commitmentId}`)
      .then((res) => res.data);
  }

  deleteRepresentatives(shareholderId: number, representatives: number[]) {
    return this.http
      .post(`/shareholder/${shareholderId}/delete-representatives`, representatives)
      .then((res) => res.data);
  }

  fetchBankIdStatus() {
    return this.http.get(`/auth/bankid-status`).then((res) => res.data);
  }

  changeNominalValueForTransaction(
    orgNumber: string,
    transactionlogId: number,
    nominalValue: number,
  ) {
    return this.http
      .post(`/transaction/${orgNumber}/${transactionlogId}`, { nominalValue })
      .then((res) => res.data);
  }

  updateCompanyRemark(orgNumber: string, remark: string | undefined) {
    return this.http.post(`/company/${orgNumber}/remark`, { remark });
  }

  copyShareholders(
    toOrgNo: string,
    fromOrgNo: string,
    shareholderIds: number[],
  ): Promise<CopyShareholdersResponse> {
    return this.http
      .post(`/shareholder/${toOrgNo}/copy`, { toOrgNo, fromOrgNo, shareholderIds })
      .then((res) => res.data);
  }

  createEnterpriseAccount(request: EnterpriseAccountRequest) {
    return this.http.put(`/enterprise-accounts`, request).then((res) => res.data);
  }

  updateEnterpriseAccount(id: string, request: EnterpriseAccountRequest) {
    return this.http.post(`/enterprise-accounts/${id}`, request).then((res) => res.data);
  }
}

export type ShareholderNotification = {
  shareholderId: number;
  email: string;
};

export type ShareholderContributionDetailsResponse = {
  contribution: ShareholderContribution;
  fileUploadConfig: FileUploadConfig;
  attachments: UploadedAttachment[];
  registeredBy: string;
};

export type ShareholderCommitmentDetailsResponse = {
  commitment: ShareholderCommitment;
  fileUploadConfig: FileUploadConfig;
  attachments: UploadedAttachment[];
  registeredBy: string;
};

export type BeneficialOwnersSearchResponse = {
  searchResults: BeneficialOwner[];
};

export type CopyShareholdersResponse = {
  shareholderIdMapping: {
    fromShareholderId: number;
    toShareholderId: number;
  }[];
};

export type UploadShareholdersExcelResponse = {
  shareholderIds: Array<number>;
};

export type EnterpriseAccountRequest = {
  name: string;
  status: EnterpriseAccountStatus;
  subscriptionLimits: Record<string, number>;
  startTime: string;
  endTime?: string;
  contactName?: string;
  contactEmail?: string;
  primaryCompanyOrgNo: string;
  metadata: EnterpriseAccountMetaData;
};
